import h from 'hyperscript';
import generateIcon from '../../_particles/icon/icon';

/**
 * @typedef {{
 *  classes: {
 *    element: string,
 *    initializedModifier: string
 *    moreButton: string,
 *    contentWrapper: string,
 *    sliderText: string,
 *    sliderTextContainer: string,
 *    clampModifier: string
 *  },
 *  initialTextHeight: number,
 *  showMoreText: string,
 *  showMoreIconOpen: string,
 *  showMoreIconClose: string,
 * }} SliderContentOptions
 */

export default class SliderContentTeaser {
  /** @type SliderContentOptions */
  static DefaultOptions = {
    classes: {
      element: 'js-slider-content-teaser',
      initializedModifier: 'slider-content-teaser--initialized',
      moreButton: 'slider-content-teaser__more-button',
      contentWrapper: 'slider-content-teaser__wrapper',
      sliderText: 'slider-content-teaser__text',
      sliderTextContainer: 'slider-content-teaser__text-container',
      clampModifier: 'slider-content-teaser__text-container--clamped',
    },
    initialTextHeight: 210,
    showMoreIconOpen: 'open',
    showMoreIconClose: 'minus',
  };

  /**
   * Creates a new instance of SliderContentTeaser
   * @param {HTMLElement} $el The HTML element the component is built from
   * @param {SliderContentOptions} config Additional options to the component
   */
  constructor($el, config = {}) {
    /** @type {HTMLElement} */
    this.$el = $el;
    /** @type {SliderContentOptions} */
    this.config = {
      ...SliderContentTeaser.DefaultOptions,
      showMoreText: $el.dataset.showMoreText || 'Mehr erfahren',
      ...config,
    };

    this.init()
      .then(() => {
        this.$el.classList.add(this.config.classes.initializedModifier);
        this.onresize();
      });
  }

  async init() {
    this.$showMoreButton = this.$el.querySelector(`.${this.config.classes.moreButton}`);
    this.$contentWrapper = this.$el.querySelector(`.${this.config.classes.contentWrapper}`);
    this.$textContainer = this.$el.querySelector(`.${this.config.classes.sliderTextContainer}`);
    this.$sliderText = this.$el.querySelector(`.${this.config.classes.sliderText}`);

    // Generate showMore-Button if not available
    if (!this.$showMoreButton) {
      this.$showMoreButton = h(`button.${this.config.classes.moreButton}`, {
        type: 'button',
      },
      generateIcon({
        icon: 'open',
        classes: [this.config.classes.moreButtonIcon],
      }),
      h(`span.${this.config.classes.moreButton}-text`, this.config.showMoreText));
    }
    this.$showMoreIcon = this.$showMoreButton.querySelector('use');

    this.config.initialTextHeight = this.$textContainer.clientHeight;
    this.$showMoreButton.addEventListener('click', () => this.toggleText());
    window.addEventListener('resize', this.onresize.bind(this));
  }

  showMoreButton(display = true) {
    if (display) {
      this.$contentWrapper.insertBefore(
        this.$showMoreButton,
        this.$textContainer.nextElementSibling,
      );
    } else if (this.$contentWrapper.contains(this.$showMoreButton)) {
      this.$contentWrapper.removeChild(this.$showMoreButton);
    }
  }

  toggleText() {
    const expanded = this.$textContainer.getAttribute('aria-expanded') === 'true';
    this.$textContainer.setAttribute('aria-expanded', !expanded);

    this.$showMoreIcon.setAttribute(
      'xlink:href',
      `#icon-${expanded ? this.config.showMoreIconOpen : this.config.showMoreIconClose}`,
    );

    if (expanded) {
      this.$textContainer.classList.add(this.config.classes.clampModifier);
    } else {
      this.$textContainer.classList.remove(this.config.classes.clampModifier);
    }
  }

  onresize() {
    const expanded = this.$textContainer.getAttribute('aria-expanded') === 'true';
    const textClamp = this.$sliderText.clientHeight > this.$textContainer.clientHeight;

    if (textClamp && !expanded) {
      this.showMoreButton();
      this.$textContainer.classList.add(this.config.classes.clampModifier);
    } else if (!(textClamp || expanded)) {
      this.showMoreButton(false);
      this.$textContainer.classList.remove(this.config.classes.clampModifier);
    } else if (expanded && !textClamp) {
      this.showMoreButton(false);
      this.toggleText();
    }
  }

  static InitializeDefaults() {
    const { element, initializedModifier } = SliderContentTeaser.DefaultOptions.classes;

    document
      .querySelectorAll(`.${element}:not(.${initializedModifier})`)
      .forEach($sliderTeaser => new SliderContentTeaser($sliderTeaser));
  }
}

SliderContentTeaser.InitializeDefaults();
